import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { missingConditionFields } from '../../shared/data-sheet-filters.codec';
import { TdbOverride } from '../../shared/tcdl-ops';
import { ConditionLibrary } from '../../models/object-mapping.model';
import { StepConditionEditorComponent } from '../step-condition-editor/step-condition-editor.component';

/** Obergrenze des Servers (`StepConditionValidator`); mehr Bedingungen lehnt er mit 400 ab. */
export const MAX_STEP_CONDITIONS = 10;

/**
 * „Ausführen wenn“ eines Modulaufrufs als UND-Liste. Echo-Vertrag wie
 * {@code app-step-condition-editor}: Jede Eingabe kommt als ganze neue Liste über
 * {@link conditionsChange}, angezeigt wird, was der Aufrufer zurückgibt.
 */
@Component({
  selector: 'app-step-condition-list',
  standalone: true,
  imports: [TranslatePipe, StepConditionEditorComponent],
  templateUrl: './step-condition-list.component.html',
  styleUrl: './step-condition-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepConditionListComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly conditions = input.required<readonly TdbOverride[]>();
  readonly scopeId = input.required<string>();
  readonly disabled = input(false);
  readonly library = input<readonly ConditionLibrary[] | null>(null);

  readonly conditionsChange = output<TdbOverride[]>();

  readonly max = MAX_STEP_CONDITIONS;
  readonly active = computed(() => this.conditions().length > 0);
  readonly full = computed(() => this.conditions().length >= MAX_STEP_CONDITIONS);
  readonly complete = computed(
    () => this.active() && this.conditions().every((row) => missingConditionFields(row).length === 0),
  );

  toggle(): void {
    this.conditionsChange.emit(this.active() ? [] : [{ subj: 'WERT' }]);
  }

  add(): void {
    if (this.full()) return;
    this.conditionsChange.emit([...this.conditions(), { subj: 'WERT' }]);
  }

  remove(index: number): void {
    this.conditionsChange.emit(this.conditions().filter((_, i) => i !== index));
  }

  update(index: number, patch: Partial<TdbOverride>): void {
    this.conditionsChange.emit(this.conditions().map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  /** Setzt den Fokus auf das erste fehlende Feld aller Zeilen; false, wenn nichts fehlt. */
  focusFirstMissing(): boolean {
    const field = this.host.nativeElement.querySelector<HTMLElement>('[aria-invalid="true"]');
    field?.focus();
    return !!field;
  }
}
